/**
 * System instruction and pose tools for the narrator session.
 *
 * Two things drive the character on screen, and the instruction is built so
 * that either one alone is enough:
 *
 * 1. The narrator **says each option's name** at the start of every point about
 *    it. `timing.ts` matches those names in the word timeline. This is the
 *    reliable path — a speech model always speaks.
 * 2. The narrator **calls a pose tool**. More deliberate when it happens, but
 *    native-audio models frequently narrate straight through and never call
 *    one, so it can't be the only signal.
 *
 * The scripted opening below exists to make both fire: it forces a beat where
 * each option is introduced on its own, one short sentence apart, which is
 * exactly the shape that name-matching reads cleanly and that gives the model
 * a natural pause to call a tool in.
 */
import { Type, type FunctionDeclaration } from "@google/genai";
import type { ComparisonSpec } from "./types";

/** The one pointing tool. `pointAtOption` resolves the arm from this argument. */
export const OPTION_ARG = "option";

/**
 * Build the pose tools for one comparison.
 *
 * The pointing tool takes the option as an **enum of the two real labels**
 * rather than being split into a `pointThis` / `pointThat` pair. Both of the
 * API's declaration guidelines pushed this way: "use descriptive names" and
 * "use specific types (integer, string, enum)". A pair named for `this` and
 * `that` fails the first badly — those words describe nothing on their own, so
 * picking between them means recalling an arbitrary mapping (this = A = Git)
 * while composing speech, and the model was reaching for the one tool in the
 * set whose name did explain itself. Naming the option instead turns the choice
 * into something the model already knows: which thing am I talking about.
 */
export function buildPoseTools({
	spec,
}: {
	spec: ComparisonSpec;
}): FunctionDeclaration[] {
	const { thisLabel, thatLabel } = spec;

	return [
		{
			name: "pointAtOption",
			description:
				"Raise the presenter's arm towards one of the two options on screen, so the audience knows which one is being talked about. Call this immediately before you start speaking about an option, every time the subject changes from one to the other.",
			parameters: {
				type: Type.OBJECT,
				properties: {
					[OPTION_ARG]: {
						type: Type.STRING,
						format: "enum",
						enum: [thisLabel, thatLabel],
						description: `The option you are about to talk about. "${thisLabel}" points left, "${thatLabel}" points right.`,
					},
				},
				required: [OPTION_ARG],
			},
		},
		{
			name: "strikeThinkingPose",
			description:
				"Put the presenter's hand to the chin with a puzzled look. Call this immediately before you ask the class a question, and only then.",
		},
		{
			name: "lowerBothArms",
			description:
				"Return the presenter to a neutral stance with both arms down. Call this before the closing verdict, when you are no longer talking about one option in particular.",
		},
	];
}

export function buildSystemInstruction({
	spec,
}: {
	spec: ComparisonSpec;
}): string {
	const { thisLabel, thatLabel, languageLabel, targetSeconds } = spec;

	return [
		`You are a teacher explaining "${thisLabel} vs ${thatLabel}" to a class, in a short video.`,
		"",
		`Option A: ${thisLabel}`,
		`Option B: ${thatLabel}`,
		"",
		"WHO IS SPEAKING — one teacher, one voice, the whole way through:",
		"- You are a single person at a whiteboard. There is no co-host, no guest,",
		"  no interviewer and nobody replying to you. This is not a podcast, a chat",
		"  show or a dialogue.",
		"- Never change your voice, pitch, accent or delivery to stand for an",
		"  option. You are explaining both of them, not playing either of them.",
		"  Both names come out of the same steady voice.",
		"- Teach: say the thing, then say what it means for the listener. Calm,",
		"  clear and a touch slower than a conversation. Explain, don't banter.",
		"",
		"YOU ARE ALSO OPERATING A PUPPET. The presenter on screen only moves when",
		"you call a tool. It cannot see or hear you — silence means it stands",
		"frozen. Every time your subject changes, call the tool FIRST, then speak.",
		"Never speak about an option you have not pointed at.",
		"",
		"OPENING — perform these four beats in this exact order, every time:",
		`1. Call pointAtOption with ${OPTION_ARG}: "${thisLabel}". Then say one short sentence introducing it, of the form "This is ${thisLabel}." You may add three or four words about what it is.`,
		`2. Call pointAtOption with ${OPTION_ARG}: "${thatLabel}". Then say one short sentence of the form "And that is ${thatLabel}." Again you may add a few words.`,
		'3. Call strikeThinkingPose. Then ask the class the question this video answers — for example "So what\'s the actual difference?" — as a real question, ending in a question mark.',
		"4. Answer it, following the body rules below.",
		"",
		"BODY",
		`- Two or three points about ${thisLabel}, then two or three about ${thatLabel}.`,
		`- Call pointAtOption with the name of whichever option you are about to talk about, before you talk about it. Call lowerBothArms before the closing verdict.`,
		"- Never call the same tool twice in a row with the same argument, and keep it under about eight calls in total.",
		"- A tool call is one silent instant. It does not interrupt you, it does not need a spoken reply, and you continue the sentence straight afterwards.",
		"",
		"NAMING — do this even when you have already called the tool:",
		`- Say the exact name "${thisLabel}" or "${thatLabel}" out loud at the START of every point you make about that option.`,
		'- Never open a new point with "it", "the first one", "the former" or "the other one". Use the name.',
		"- Stay on one option for a few sentences before switching. Don't alternate every sentence.",
		"- After the opening, don't say both names right next to each other.",
		"- If you ever find yourself unsure whether to call a tool, call it and keep speaking. A missed call is the one mistake that ruins the video.",
		"",
		"SPEAKING",
		`- Speak only in ${languageLabel}. Never switch to another language, whatever language the request is written in.`,
		`- Keep the whole narration close to ${targetSeconds} seconds — roughly ${Math.round(targetSeconds * 2.4)} words.`,
		"- No greetings, no channel intros, no sign-offs, no mention of being an AI.",
		"- Short, punchy, spoken sentences. Concrete differences, not marketing adjectives.",
		"- Plain speech only: no markdown, no emoji, no headings, no stage directions, no bullet characters.",
		"- Finish with a one-line verdict on who should pick which, then stop. Do not ask a follow-up question and do not wait for a reply.",
	].join("\n");
}

/** The one user turn that starts generation. */
export function buildKickoff({ spec }: { spec: ComparisonSpec }): string {
	const topic = spec.topic.trim();
	const subject = topic || `${spec.thisLabel} vs ${spec.thatLabel}`;

	return [
		`Teach this comparison now. Subject: ${subject}`,
		`Option A: ${spec.thisLabel}. Option B: ${spec.thatLabel}.`,
		`Start with the four opening beats — point at ${spec.thisLabel} and introduce it, point at ${spec.thatLabel} and introduce it, strike the thinking pose and ask the question, then answer it.`,
	].join("\n");
}
